import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { InventoryRawMaterialViewModel } from "../../../viewmodels/inventory/rawMaterial/InventoryRawMaterialViewModel";

import { connect } from "react-redux";

import * as actions from "../../../store/actions/index";

import { withRouter } from "../../../shared/utility";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import SelectMenu from "../../../components/controls/SelectMenu";
import OperationalDataTableSkeleton from "../../../components/operational/OperationalDataTableSkeleton";
import OperationalAnalyticsStrip from "../../../components/operational/OperationalAnalyticsStrip";
import OperationalTableToolbar from "../../../components/operational/OperationalTableToolbar";
import InventoryRawMaterialSubTable from "../../../components/inventory/InventoryRawMaterialSubTable";
import Button from "../../../components/ui/Button";
import EmptyModal from "../../../components/modals/EmptyModal";
import PromoteReceivingToPackagingModal from "../../../components/inventory/PromoteReceivingToPackagingModal";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { formatCurrency, generateUniqueNumber } from "../../../shared/utility";
import { formatUnitOfMeasure } from "../../../shared/formatUnitOfMeasure";
import {
  fetchLocationZonesForDropdown,
  fetchLocationsForDropdown,
} from "../../../shared/locationDropdowns";

const ALL_VALUE = 0;
const FIELD_SELECT_CLASS = "w-[16rem] min-w-[16rem] max-w-[16rem] shrink-0";
const FIELD_PANEL_CLASS = "inventory-filter-select-panel";

function FilterField({ label, children }) {
  return (
    <div className="flex w-[16rem] min-w-[16rem] max-w-[16rem] shrink-0 flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
        {label}
      </span>
      {children}
    </div>
  );
}

function formatAmountInStock(row) {
  return `${row.total_stock} ${row.unit_of_measure.label}`;
}

function formatCategoryName(categoryName) {
  if (categoryName == null || categoryName === "") {
    return "Unknown";
  }
  return categoryName;
}

function bomNameTemplate(row) {
  return (
    <div>
      <div className="font-semibold text-[#0F172A]">{row.bom_name || "—"}</div>
      <div className="text-xs text-[#64748B]">PLU: {row.bom_plu || "—"}</div>
    </div>
  );
}

function InventoryRawMaterialScreen(props) {
  const { logout, addSnackbar } = props;
  const {
    getInStockRawMaterial,
    fetchLocations,
    fetchZones,
    fetchLots,
    fetchCartons,
    loading,
    loadingLocations,
    loadingZones,
    loadingLots,
    loadingCartons,
    products,
    locationDetails,
    zoneDetails,
    lotDetails,
    cartonDetails,
    locationGroupKey,
    zoneGroupKey,
    lotGroupKey,
    cartonKey,
    onUpdateReceivingItemStock,
    getProductTypes,
    productTypeOptions,
    exportNetSuiteRawInventoryCsv,
    getPromoteReceivingDefaults,
    promoteReceivingToPackaging,
  } = InventoryRawMaterialViewModel(logout);

  const currentPageRef = useRef(1);
  const [locationId, setLocationId] = useState(ALL_VALUE);
  const [zoneId, setZoneId] = useState(ALL_VALUE);
  const [productTypeId, setProductTypeId] = useState(ALL_VALUE);
  const [locationOptions, setLocationOptions] = useState([
    { value: ALL_VALUE, label: "All locations" },
  ]);
  const [zoneOptions, setZoneOptions] = useState([
    { value: ALL_VALUE, label: "All zones" },
  ]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [expandedRows, setExpandedRows] = useState(null);
  const [appliedSearch, setAppliedSearch] = useState(null);
  const [promoteModal, setPromoteModal] = useState({
    open: false,
    receivingItemId: null,
  });

  const searchRef = useRef();

  const selectedLocationId = locationId > 0 ? locationId : null;
  const selectedZoneId = zoneId > 0 ? zoneId : null;
  const selectedProductTypeId = productTypeId > 0 ? productTypeId : null;
  const hasActiveFilters = Boolean(
    selectedLocationId || selectedZoneId || selectedProductTypeId,
  );

  const productTypeSelectOptions = [
    { value: ALL_VALUE, label: "All product types" },
    ...productTypeOptions.filter(
      (option) => option.value !== ALL_VALUE && option.value !== -1,
    ),
  ];

  useEffect(() => {
    getInStockRawMaterial(null, currentPageRef.current, null, null, null);
    getProductTypes();
    fetchLocationsForDropdown().then((result) => {
      if (result.success) {
        setLocationOptions([
          { value: ALL_VALUE, label: "All locations" },
          ...(result.data ?? []),
        ]);
      } else if (result.errorStatus === 401) {
        logout();
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedLocationId) {
      setZoneOptions([{ value: ALL_VALUE, label: "All zones" }]);
      return;
    }

    let cancelled = false;
    fetchLocationZonesForDropdown({ value: selectedLocationId }).then(
      (result) => {
        if (cancelled || !result.success) {
          if (result.errorStatus === 401) {
            logout();
          }
          return;
        }
        setZoneOptions([
          { value: ALL_VALUE, label: "All zones" },
          ...(result.data ?? []),
        ]);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  const getSearchValue = () => searchRef.current?.value?.trim() || null;

  const fetchPage = (
    page,
    nextLocationId = selectedLocationId,
    nextZoneId = selectedZoneId,
    nextProductTypeId = selectedProductTypeId,
  ) => {
    const search = getSearchValue();
    setAppliedSearch(search);
    getInStockRawMaterial(
      search,
      page,
      nextProductTypeId,
      nextLocationId,
      nextZoneId,
    );
  };

  const resetToFirstPage = () => {
    currentPageRef.current = 1;
    setFirst(0);
    setExpandedRows(null);
  };

  const handleSearch = () => {
    resetToFirstPage();
    fetchPage(1);
  };

  const handlePagination = (event) => {
    const page = event.page + 1;
    currentPageRef.current = page;
    setFirst(event.first);
    setRows(event.rows);
    setExpandedRows(null);
    fetchPage(page);
  };

  const handleLocationChange = (event) => {
    const nextLocation = event.target.value?.value ?? ALL_VALUE;
    const nextLocationId = nextLocation > 0 ? nextLocation : null;
    resetToFirstPage();
    setLocationId(nextLocation > 0 ? nextLocation : ALL_VALUE);
    setZoneId(ALL_VALUE);
    fetchPage(1, nextLocationId, null, selectedProductTypeId);
  };

  const handleZoneChange = (event) => {
    const nextZone = event.target.value?.value ?? ALL_VALUE;
    const nextZoneId = nextZone > 0 ? nextZone : null;
    resetToFirstPage();
    setZoneId(nextZone > 0 ? nextZone : ALL_VALUE);
    fetchPage(1, selectedLocationId, nextZoneId, selectedProductTypeId);
  };

  const handleProductTypeChange = (event) => {
    const nextType = event.target.value?.value ?? ALL_VALUE;
    const nextProductTypeId = nextType > 0 ? nextType : null;
    resetToFirstPage();
    setProductTypeId(nextType > 0 ? nextType : ALL_VALUE);
    fetchPage(1, selectedLocationId, selectedZoneId, nextProductTypeId);
  };

  const handleClearFilters = () => {
    resetToFirstPage();
    setLocationId(ALL_VALUE);
    setZoneId(ALL_VALUE);
    setProductTypeId(ALL_VALUE);
    fetchPage(1, null, null, null);
  };

  const closePromoteModal = () => {
    setPromoteModal({ open: false, receivingItemId: null });
  };

  const openPromoteModal = (receivingItemId) => {
    setPromoteModal({ open: true, receivingItemId });
  };

  const handlePromoteSuccess = () => {
    closePromoteModal();
    addSnackbar(
      new SnackbarData(
        generateUniqueNumber(),
        "Item promoted to sellable stock",
        SnackbarType.Success,
      ),
    );
    setExpandedRows(null);
    fetchPage(currentPageRef.current);
  };

  const onRowExpand = async (event) => {
    const rowData = event.data;
    const key = locationGroupKey(
      rowData.id,
      appliedSearch,
      selectedProductTypeId,
      selectedLocationId,
      selectedZoneId,
    );
    if (!locationDetails[key]) {
      await fetchLocations(
        rowData,
        appliedSearch,
        selectedProductTypeId,
        selectedLocationId,
        selectedZoneId,
      );
    }
  };

  const updateCartonStock = (
    id,
    newInventory,
    oldInventory,
    oldItemCost,
    newItemCost,
    reason,
    carton,
  ) =>
    onUpdateReceivingItemStock(
      id,
      newInventory,
      oldInventory,
      oldItemCost,
      newItemCost,
      reason,
      {
        ingredientId: carton.ingredient_id,
        locationId: carton.location_id,
        zoneId: carton.zone_id,
        lotNumber: carton.lot_number,
        search: appliedSearch,
        productTypeId: selectedProductTypeId,
        locationFilter: selectedLocationId,
        zoneFilter: selectedZoneId,
      },
    );

  const allowExpansion = () => true;

  const handleBarcodeCopied = (success = true) => {
    addSnackbar(
      new SnackbarData(
        generateUniqueNumber(),
        success ? "Barcode copied" : "Could not copy barcode",
        success ? SnackbarType.Success : SnackbarType.Error,
      ),
    );
  };

  const rowExpansionTemplate = (data) => {
    const key = locationGroupKey(
      data.id,
      appliedSearch,
      selectedProductTypeId,
      selectedLocationId,
      selectedZoneId,
    );

    return (
      <InventoryRawMaterialSubTable
        groups={locationDetails[key]}
        loading={loadingLocations[key]}
        zonesByKey={zoneDetails}
        loadingZones={loadingZones}
        zoneKeyFor={(location) =>
          zoneGroupKey(
            location.ingredient_id,
            location.location_id,
            appliedSearch,
            selectedProductTypeId,
            selectedZoneId,
          )
        }
        onExpandLocation={(location) => {
          const zoneCacheKey = zoneGroupKey(
            location.ingredient_id,
            location.location_id,
            appliedSearch,
            selectedProductTypeId,
            selectedZoneId,
          );
          if (!zoneDetails[zoneCacheKey]) {
            fetchZones(
              location,
              appliedSearch,
              selectedProductTypeId,
              selectedZoneId,
            );
          }
        }}
        lotsByKey={lotDetails}
        loadingLots={loadingLots}
        lotKeyFor={(zone) =>
          lotGroupKey(
            zone.ingredient_id,
            zone.location_id,
            zone.zone_id,
            appliedSearch,
            selectedProductTypeId,
          )
        }
        onExpandZone={(zone) => {
          const lotCacheKey = lotGroupKey(
            zone.ingredient_id,
            zone.location_id,
            zone.zone_id,
            appliedSearch,
            selectedProductTypeId,
          );
          if (!lotDetails[lotCacheKey]) {
            fetchLots(zone, appliedSearch, selectedProductTypeId);
          }
        }}
        cartonsByKey={cartonDetails}
        loadingCartons={loadingCartons}
        cartonKeyFor={(lot) =>
          cartonKey(
            lot.ingredient_id,
            lot.location_id,
            lot.zone_id,
            lot.lot_number,
            appliedSearch,
            selectedProductTypeId,
          )
        }
        onExpandLot={(lot) => {
          const cartonCacheKey = cartonKey(
            lot.ingredient_id,
            lot.location_id,
            lot.zone_id,
            lot.lot_number,
            appliedSearch,
            selectedProductTypeId,
          );
          if (!cartonDetails[cartonCacheKey]) {
            fetchCartons(lot, appliedSearch, selectedProductTypeId);
          }
        }}
        unitOfMeasure={data.unit_of_measure}
        onUpdateReceivingItemStock={updateCartonStock}
        onPromote={openPromoteModal}
        onBarcodeCopied={handleBarcodeCopied}
      />
    );
  };

  const totalRecords = products?.total ?? 0;
  const resultMeta = `${totalRecords.toLocaleString()} BOM item${totalRecords === 1 ? "" : "s"}`;

  const analyticsStats = [
    {
      name: "Total Raw In Stock",
      stat: formatCurrency(products.totalStockDollarAmount),
      subtitle: "Across all BOMs",
      icon: "targetWeight",
    },
    {
      name: "Page Total Raw In Stock",
      stat: formatCurrency(products.totalPageStockDollarAmount),
      subtitle: "Current page",
      icon: "manufacturedWeight",
    },
  ];

  const emptyMessage = (
    <div className="px-6 py-12 text-center">
      <p className="text-sm text-[#64748B]">
        No raw material inventory matches your search or filters.
      </p>
    </div>
  );

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div className="space-y-5 px-4 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                <li>
                  <Link
                    to="/inventory-raw-material"
                    className="text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Inventory
                  </Link>
                </li>
                <li className="text-[#CBD5E1]" aria-hidden="true">
                  /
                </li>
                <li className="text-[#475569]">Raw Material</li>
              </ol>
            </nav>
            <h1 className="mt-2 text-xl font-semibold tracking-tight text-[#0F172A]">
              In stock Raw Material
            </h1>
          </div>

          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={exportNetSuiteRawInventoryCsv}
          >
            Export CSV
          </Button>
        </header>

        <OperationalAnalyticsStrip stats={analyticsStats} loading={loading} />

        <div className="overflow-visible rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <OperationalTableToolbar
            searchRow={
              <>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  name="searchRawMaterial"
                  id="searchRawMaterial"
                  className="block min-h-10 w-full rounded-xl border border-[#E2E8F0] bg-white py-2 pl-10 pr-3 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
                  placeholder="Search by BOM name or PLU..."
                  ref={searchRef}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </>
            }
            footer={
              <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
                <FilterField label="Location">
                  <SelectMenu
                    handleInputChange={handleLocationChange}
                    parentClass={FIELD_SELECT_CLASS}
                    panelClassName={FIELD_PANEL_CLASS}
                    placeholder="All locations"
                    name="location"
                    options={locationOptions}
                    value={locationId}
                  />
                </FilterField>
                <FilterField label="Zone">
                  <SelectMenu
                    handleInputChange={handleZoneChange}
                    parentClass={FIELD_SELECT_CLASS}
                    panelClassName={FIELD_PANEL_CLASS}
                    placeholder="All zones"
                    name="zone"
                    options={zoneOptions}
                    value={zoneId}
                    disabled={!selectedLocationId}
                  />
                </FilterField>
                <FilterField label="Product type">
                  <SelectMenu
                    handleInputChange={handleProductTypeChange}
                    parentClass={FIELD_SELECT_CLASS}
                    panelClassName={FIELD_PANEL_CLASS}
                    placeholder="All product types"
                    name="product_type"
                    options={productTypeSelectOptions}
                    value={productTypeId}
                  />
                </FilterField>
                {hasActiveFilters ? (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="mb-2 text-xs font-semibold text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            }
            trailing={
              <span className="text-xs font-medium text-[#64748B]">
                {resultMeta}
              </span>
            }
          />

          <div className="operational-embedded-table overflow-hidden">
            {loading ? (
              <OperationalDataTableSkeleton
                columns={[
                  "BOM",
                  "Category",
                  "Amount in stock",
                  "Total Cost",
                  "",
                ]}
                rows={10}
              />
            ) : (
              <DataTable
                value={products.data ?? []}
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                className="p-datatable-sm text-sm"
                emptyMessage={emptyMessage}
              >
                <Column
                  header="BOM"
                  body={bomNameTemplate}
                  style={{ minWidth: "12rem" }}
                />
                <Column
                  header="Category"
                  body={(row) => formatCategoryName(row.category_name)}
                  style={{ minWidth: "9rem" }}
                />
                <Column
                  header="Amount in stock"
                  alignHeader="right"
                  body={(row) => formatAmountInStock(row)}
                  style={{ minWidth: "10rem", textAlign: "right" }}
                  bodyClassName="text-right text-[#0F172A]"
                />
                <Column
                  header="Total Cost"
                  alignHeader="right"
                  body={(row) => (
                    <span className="tabular-nums font-medium text-[#0F172A]">
                      {formatCurrency(row.total_cost)}
                    </span>
                  )}
                  style={{ minWidth: "8rem", textAlign: "right" }}
                  bodyClassName="text-right tabular-nums"
                />
                <Column
                  expander={allowExpansion}
                  header=""
                  style={{ width: "5rem" }}
                />
              </DataTable>
            )}
          </div>

          <div className="border-t border-[#E2E8F0] px-4 py-4 sm:px-5">
            <Paginator
              className="operational-embedded-paginator"
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              onPageChange={handlePagination}
            />
          </div>
        </div>
      </div>
      <EmptyModal
        wide
        childComponent={
          promoteModal.receivingItemId ? (
            <PromoteReceivingToPackagingModal
              receivingItemId={promoteModal.receivingItemId}
              onSuccess={handlePromoteSuccess}
              onCancel={closePromoteModal}
              getPromoteReceivingDefaults={getPromoteReceivingDefaults}
              promoteReceivingToPackaging={promoteReceivingToPackaging}
              logout={logout}
            />
          ) : null
        }
        open={promoteModal.open}
        setOpen={(next) => {
          if (!next.open) {
            closePromoteModal();
          }
        }}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(InventoryRawMaterialScreen),
);
